import * as React from 'react';
import type { GoogleAnalyticsTrackingStatus } from '@bondvet/types/booking';
import { graphql, useStaticQuery } from 'gatsby';
import type {
    Maybe,
    GoogleAdwordsSettings,
    Analytics4Settings,
    GoogleSettings,
} from '@types/gatsby-types';
import { IS_BROWSER } from 'core/constants';
import { isAutomatedTest } from 'core/lib/isAutomatedTest';
import { getClientInfo } from '../lib/clientInfo';

let initializedGoogleAnalytics4Id: string | null = null;
let initializedGoogleAnalytics4UserId: string | null = null;

const GOOGLE_ANALYTICS_EVENT_TIMEOUT_MS = 2000;

const query = graphql`
    query GoogleAnalyticsSettingsQuery {
        settings {
            google {
                adwords {
                    id
                }
                analytics4 {
                    id
                    cookieDomain
                    enabled
                }
                conversionEvent
            }
        }
    }
`;

type GoogleAnalyticsSettingsQuery = {
    settings: Maybe<{
        google: Maybe<
            Pick<GoogleSettings, 'conversionEvent'> & {
                adwords: Maybe<GoogleAdwordsSettings>;
                analytics4: Maybe<Analytics4Settings>;
            }
        >;
    }>;
};

type GoogleAnalyticsOptions = {
    send_to: string;
    value: number;
    currency: string;
    event_callback?: () => void;
};

type GoogleAnalyticsSettings = {
    hasGa: boolean;
    hasGa4: boolean;
};

type TrackWithGA = (
    trackInAutomatedTest: boolean,
    cmd: string,
    ...args: unknown[]
) => void;

type UseGoogleAnalytics = {
    getGoogleAnalyticsTrackingStatus: () => Promise<GoogleAnalyticsTrackingStatus>;
    trackWithAnyGoogleAnalytics: TrackWithGA;
    trackWithGoogleAnalytics4: TrackWithGA;
    trackGA4OnPageLoad: () => void;
    trackGoogleConversion: (url?: Location | (string & Location)) => void;
    conversionEvent?: string;
    analytics4Id?: string;
};

function shouldTrack(trackInAutomatedTest: boolean): boolean {
    if (trackInAutomatedTest) {
        return true;
    }

    if (isAutomatedTest()) {
        // eslint-disable-next-line no-console
        console.log(
            'automated test detected - skipping Google Analytics tracking',
        );
        return false;
    }

    return true;
}

export default function useGoogleAnalytics(): UseGoogleAnalytics {
    const { settings } = useStaticQuery<GoogleAnalyticsSettingsQuery>(query);
    const conversionEvent = settings?.google?.conversionEvent;
    const analytics4Id = settings?.google?.analytics4?.id;

    const gaSettings = React.useMemo<GoogleAnalyticsSettings>(() => {
        const hasGa =
            settings?.google?.analytics4?.enabled &&
            IS_BROWSER &&
            typeof window.gtag === 'function';
        const hasGa4 = hasGa && settings?.google?.analytics4?.id;

        return {
            hasGa,
            hasGa4,
        };
    }, [
        settings?.google?.analytics4?.enabled,
        settings?.google?.analytics4?.id,
    ]);

    const getGoogleAnalyticsTrackingStatus = React.useCallback(() => {
        return new Promise<GoogleAnalyticsTrackingStatus>((resolvePromise) => {
            if (gaSettings.hasGa) {
                let resolved = false;
                const startTime = new Date().getTime();
                window.gtag?.('event', 'test_blocked_tracking', {
                    event_callback: () => {
                        const durationMs = new Date().getTime() - startTime;
                        // eslint-disable-next-line no-console
                        console.info(
                            `Google Analytics responded within ${durationMs}ms.`,
                        );

                        resolved = true;
                        resolvePromise({
                            enabled: true,
                            blocked: false,
                        });
                    },
                });
                setTimeout(() => {
                    if (!resolved) {
                        // eslint-disable-next-line no-console
                        console.info(
                            'Google Analytics did not respond within ' +
                                `${GOOGLE_ANALYTICS_EVENT_TIMEOUT_MS}ms, assuming it's blocked.`,
                        );

                        resolvePromise({
                            enabled: true,
                            blocked: true,
                        });
                    }
                    // add a grace period to make sure the event callback is called
                }, GOOGLE_ANALYTICS_EVENT_TIMEOUT_MS);
            } else {
                resolvePromise({ enabled: false, blocked: false });
            }
        });
    }, [gaSettings.hasGa]);

    const trackWithAnyGoogleAnalytics = React.useCallback(
        (trackInAutomatedTest: boolean, cmd: string, ...args: unknown[]) => {
            if (gaSettings.hasGa && shouldTrack(trackInAutomatedTest)) {
                // _we_ know that `window.gtag` is a function, due
                // to `gaSettings.hasGa` being true, but typescript doesn't
                // know that, so we keep the optional chaining
                try {
                    window.gtag?.(cmd, ...args);
                } catch (error) {
                    console.warn(
                        'error submitting GA command',
                        (error as Error).message,
                    );
                }
            }
        },
        [gaSettings.hasGa],
    );

    const trackWithGoogleAnalytics4 = React.useCallback(
        (trackInAutomatedTest: boolean, cmd: string, ...args: unknown[]) => {
            if (gaSettings.hasGa4 && shouldTrack(trackInAutomatedTest)) {
                // `gaSettings.hasGa4` being `true` means that `gaSettings.hasGa` is
                // `true` as well. but again, TypeScript doesn't
                // know that, hence the optional chaining
                try {
                    window.gtag?.(cmd, ...args);
                } catch (error) {
                    console.warn(
                        'error submitting GA4 command',
                        (error as Error).message,
                    );
                }
            }
        },
        [gaSettings.hasGa4],
    );

    const trackGA4OnPageLoad = React.useCallback(() => {
        if (gaSettings.hasGa4) {
            const ga4Id = settings?.google?.analytics4?.id;
            const adwordsId = settings?.google?.adwords?.id;

            if (initializedGoogleAnalytics4Id !== ga4Id) {
                trackWithGoogleAnalytics4(true, 'config', ga4Id);

                trackWithGoogleAnalytics4(true, 'config', adwordsId);

                initializedGoogleAnalytics4Id = ga4Id || null;
            }

            const clientInfo = getClientInfo();

            if (initializedGoogleAnalytics4UserId !== clientInfo?.clientId) {
                trackWithGoogleAnalytics4(true, 'set', {
                    user_id: clientInfo?.clientId,
                });

                initializedGoogleAnalytics4UserId =
                    clientInfo?.clientId || null;
            }
        }
    }, [
        gaSettings.hasGa4,
        settings?.google?.analytics4?.id,
        settings?.google?.adwords?.id,
        trackWithGoogleAnalytics4,
    ]);

    const trackGoogleConversion = React.useCallback(
        (url?: Location | (string & Location)) => {
            if (conversionEvent) {
                if (isAutomatedTest()) {
                    // eslint-disable-next-line no-console
                    console.log(
                        'automated test detected – skipping Google Analytics conversion tracking',
                    );
                } else {
                    const options: GoogleAnalyticsOptions = {
                        send_to: conversionEvent,
                        value: 1.0,
                        currency: 'USD',
                    };

                    if (typeof url !== 'undefined') {
                        options.event_callback = () => {
                            trackGA4OnPageLoad();
                        };
                    }

                    trackWithAnyGoogleAnalytics(
                        false,
                        'event',
                        'conversion',
                        options,
                    );
                }
            }
        },
        [conversionEvent, trackWithAnyGoogleAnalytics, trackGA4OnPageLoad],
    );

    return {
        getGoogleAnalyticsTrackingStatus,
        trackWithAnyGoogleAnalytics,
        trackWithGoogleAnalytics4,
        trackGA4OnPageLoad,
        trackGoogleConversion,
        conversionEvent,
        analytics4Id,
    };
}
